import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { Crown, Layout, Settings, Trash2, User, UserCog } from 'lucide-react';
import { GeoFilterDropdown } from './GeoFilterDropdown';
import { GenderFilterDropdown } from './GenderFilterDropdown';
import { calculateDaysRemaining, isSubscriptionCancelled } from '@/utils/subscriptionHelpers';
import { ProfileSettings } from '@/components/ProfileSettings';
import { apiFetch } from '@/utils/api.ts';
import config from '@/config';

type HeaderProps = {
  connectionStatus: string;
  sessionActive?: boolean;
  isPeerAudioMuted?: boolean;
  isPeerVideoHidden?: boolean;
  onTogglePeerAudio?: () => void;
  onTogglePeerVideo?: () => void;
  onShowVideoFilters?: () => void;
  onChangeLayout?: () => void;
  onReport?: () => void;
  rtcConnectionState?: string;
};

export function Header({
  connectionStatus,
  sessionActive = false,
  isPeerAudioMuted = false,
  isPeerVideoHidden = false,
  onTogglePeerAudio,
  onTogglePeerVideo,
  onShowVideoFilters,
  onChangeLayout,
  onReport,
  rtcConnectionState
}: HeaderProps) {
  const { user, logout, getIdToken } = useAuth();
  const [ , navigate ] = useLocation();
  const [ showPlanHover, setShowPlanHover ] = useState(false);
  const [ showDeleteDialog, setShowDeleteDialog ] = useState(false);
  const [ showPlansDialog, setShowPlansDialog ] = useState(false);
  const [ showProfileSettings, setShowProfileSettings ] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCancelled = isSubscriptionCancelled(user?.maybeSubscriptionInfo?.status);
  const daysRemaining = isCancelled && user?.maybeSubscriptionInfo?.maybeExpiryDate
    ? calculateDaysRemaining(user.maybeSubscriptionInfo.maybeExpiryDate)
    : 0;
  const userPlan = user?.maybeSubscriptionInfo?.plan?.toLowerCase() || 'free';

  const handleLogout = () => {
    console.log('Header component: handling logout');
    logout();
  };

  const handlePlansClick = () => {
    setShowPlansDialog(true);
  };

  const confirmViewPlans = () => {
    setShowPlansDialog(false);
    navigate('/plans?from=chat');
  };

  const handleDeleteAccount = () => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteAccount = async () => {
    console.log('Account deletion requested');
    try {
      const token = getIdToken();
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }

      const response = await apiFetch(`${config.baseUrl}/${config.endpoints.deleteAccount}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed delete account. Please try again.');
        return;
      }

    } catch (err) {
      console.error('Error deleting account', err);
      setError('An error occurred. Please try again.');
    }
    logout();
    setShowDeleteDialog(false);
  };

  return (
    <header className="bg-surface px-4 py-3 shadow-md flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="text-primary text-2xl mr-2 h-6 w-6" viewBox="0 0 24 24"
               fill="currentColor">
            <path
              d="M17 9.2l5.213-3.65a.5.5 0 0 1 .787.41v12.08a.5.5 0 0 1-.787.41L17 14.8V19a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v4.2zm-1 3.06l5.14 3.6V8.14L16 11.74v.52zM15 6H3v12h12V6z"/>
          </svg>
          <h1 className="text-xl font-semibold">Blended</h1>
        </div>
        {/* Current Plan Indicator */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`flex items-center gap-2 px-3 py-1.5 bg-[#1C2431] border rounded-md transition-all duration-200 ${
                  isCancelled ? 'border-orange-500' : 'border-slate-700'
                } ${userPlan !== 'free' ? 'cursor-pointer hover:bg-[#273449]' : ''} ${
                  isCancelled ? 'w-auto' : 'w-[130px]'
                }`}
                onMouseEnter={() => userPlan !== 'free' && setShowPlanHover(true)}
                onMouseLeave={() => setShowPlanHover(false)}
                onClick={userPlan !== 'free' ? handlePlansClick : undefined}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  userPlan === 'free' ? 'bg-gray-500' : isCancelled ? 'bg-orange-500' : 'bg-blue-500'
                }`}></div>
                <span className="text-sm text-white font-medium text-center flex-1 whitespace-nowrap">
                  {userPlan !== 'free' && showPlanHover ? 'View Plans' :
                    userPlan === 'free' ? 'Free Plan' :
                      isCancelled ? `Pro (${daysRemaining}d left)` : 'Pro Plan'}
                </span>
              </div>
            </TooltipTrigger>
            {isCancelled && (
              <TooltipContent>
                <p>Subscription cancelled. Access expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        {userPlan === 'free' && (
          <Button
            variant="outline"
            size="sm"
            className="bg-transparent hover:bg-[#273449] text-white border border-slate-700 flex items-center gap-2"
            onClick={handlePlansClick}
          >
            <Crown className="w-4 h-4"/>
            Upgrade
          </Button>
        )}
      </div>
      {/* Right side - Control Buttons and Settings */}
      <div className="flex items-center space-x-2">
        <TooltipProvider>
          {/* Geo Filter Dropdown */}
          <GeoFilterDropdown/>
          {/* Gender Filter Dropdown */}
          <GenderFilterDropdown/>
          {/* Layout Toggle Button */}
          {onChangeLayout && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-transparent hover:bg-[#273449] text-white border border-slate-700 p-2"
                  onClick={onChangeLayout}
                >
                  <Layout className="w-4 h-4"/>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Switch between layouts
              </TooltipContent>
            </Tooltip>
          )}


        </TooltipProvider>
        {/* Settings Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent hover:bg-[#273449] text-white border border-slate-700 flex items-center gap-2"
            >
              <Settings className="w-4 h-4"/>
              Settings
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#1C2431] border-slate-700 min-w-[180px]">
            <DropdownMenuItem
              className="text-white hover:bg-[#273449] cursor-pointer"
              onClick={() => setShowProfileSettings(true)}
            >
              <UserCog className="w-4 h-4 mr-2"/>
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700"/>
            <DropdownMenuItem
              className="text-white hover:bg-[#273449] cursor-pointer"
              onClick={handleLogout}
            >
              <User className="w-4 h-4 mr-2"/>
              Sign Out
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700"/>
            <DropdownMenuItem
              className="text-red-400 hover:bg-red-900/20 hover:text-red-300 cursor-pointer"
              onClick={handleDeleteAccount}
            >
              <Trash2 className="w-4 h-4 mr-2"/>
              Delete Account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#1A2332] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Account</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete your account? This action cannot be undone. All your data will be
              permanently removed from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowDeleteDialog(false)}
              className="bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAccount}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* View Plans Confirmation Dialog */}
      <AlertDialog open={showPlansDialog} onOpenChange={setShowPlansDialog}>
        <AlertDialogContent className="bg-[#1A2332] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">View Plans</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Viewing this page will interrupt the video chat. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowPlansDialog(false)}
              className="bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmViewPlans}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              View Plans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Profile Settings */}
      <ProfileSettings
        isVisible={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />
    </header>
  );
}
